import React from 'react';
import { Content, ModalLayout, Modifier, Text } from 'components';
import { Button, Flex, Image, Select } from 'antd';
import MessageAlert, { stringToMoneyFormat } from 'utils';

const ProductCartModal = (props) => {
  /* ===== Props ===== */
  const { type, data, onClose, onOk } = props;

  /* ===== Variables ===== */
  const styles = {
    layoutStyle: {
      borderRadius: 10,
    },

    productWrap: {
      display: 'flex',
      gap: '8px',
      alignItems: 'center',
    },

    thumbnail: {
      width: 60,
      height: 60,
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      background: '#F7F7F8',
    },

    buttonWrap: {
      display: 'flex',
      alignItems: 'center',
      gap: '16px',
    },
    button: {
      flex: 1,
      height: 48,
      fontSize: 16,
      fontFamily: 'Pretendard',
      fontWeight: 700,
    },

    optionWrap: {
      display: 'flex',
      flexDirection: 'column',
      gap: '8px',
    },
    optionContainer: {
      display: 'flex',
      flexDirection: 'column',
      gap: '8px',
    },
    option: {
      flex: 1,
    },
    optionTitle: {
      fontSize: 12,
      fontWeight: 400,
      color: '#999999',
    },
  };

  /* ===== State ===== */
  // 장바구니 담을 수량
  const [cartNum, setCartNum] = React.useState(1);
  // 장바구니 담을 옵션
  const [selectedOption, setSelectedOption] = React.useState(null);
  // 상품가격 + 옵션 가격
  const [productPrice, setProductPrice] = React.useState(data.finalPrice);
  // 배송주기
  const [regularCycle, setRegularCycle] = React.useState({
    regular_cycle: null,
    max_regular_delivery_count: null,
  });

  /* ===== Hooks ===== */
  /**
   * 상품 옵션 정제
   * --
   */
  const ProductOptions = React.useMemo(() => {
    if (!data.options || !data.options.length) {
      setSelectedOption(null);
      return null;
    }

    setSelectedOption([]);

    const newOptions = data.options.map((option) => ({
      key: `${option.option_id}`,
      title: option.option_title,
      type: option.type,
      children: option.option.map((op) => ({
        label: `${op.option_name}${
          op.option_price ? ` (+${stringToMoneyFormat(op.option_price)})` : ''
        }`,
        value: `${option.option_id}-${op.option_key}`,
      })),
      origin: option,
    }));

    const groupedOptions = newOptions.reduce(
      (acc, cur) => {
        acc[cur?.type]?.push(cur);
        return acc;
      },
      { REQUIRED: [], ADDITION: [] }
    );

    // 각 그룹별로 정렬
    groupedOptions.REQUIRED.sort((a, b) => a.order - b.order);
    groupedOptions.ADDITION.sort((a, b) => a.order - b.order);
    return groupedOptions;
  }, [data.options]);

  /**
   * 옵션포함 금액 정제
   * --
   */
  React.useEffect(() => {
    if (selectedOption && selectedOption.length) {
      const total = selectedOption.reduce((acc, cur) => {
        const { option } = cur;

        acc += Number(option.option_price ?? 0);
        return acc;
      }, data.finalPrice);

      setProductPrice(total);
    }
  }, [selectedOption, data.finalPrice]);

  /* ===== Function ===== */
  /**
   * type에 따른 값 전송
   * --
   * @param {string} t
   * @param {object} v
   * @returns
   */
  const handleSubmit = (t, v) => {
    let value = v;
    // 장바구니 경우 옵션 선택 체크
    if (t === 'cart' && ProductOptions) {
      const { product_option } = v;

      // 선택되지 않은 옵션 반환
      const notSelectedOptions = ProductOptions.REQUIRED.filter(
        (option) =>
          !product_option.some(
            (select) => select.option_id.toString() === option.key
          )
      );

      if (notSelectedOptions.length) {
        MessageAlert.warning('필수 옵션을 모두 선택해주세요!');
        return;
      } else {
        const sortOption = [...selectedOption].sort(
          (a, b) => a.option_id - b.option_id
        );

        value = {
          ...v,
          product_option: sortOption.map((option) => ({
            ...option,
            option: [option.option],
          })),
        };
      }
    }

    if (v.type === 'SUBSCRIPTION') {
      if (!regularCycle.regular_cycle) {
        MessageAlert.warning('배송주기를 선택해주세요!');
        return;
      }

      if (!regularCycle.max_regular_delivery_count) {
        MessageAlert.warning('배송횟수를 선택해주세요!');
        return;
      }

      value = {
        ...value,
        ...regularCycle,
      };
    }

    onOk(t, value);
  };

  /**
   * 옵션 선택 핸들러
   * --
   */
  const handleOptionSelect = (target, optionKey) => {
    const [parentKey, childKey] = optionKey.split('-'); // 부모와 자식 키 분리

    // 부모 옵션 찾기
    const parentOption = ProductOptions[target].find(
      (option) => option.key === parentKey
    );
    if (!parentOption) {
      console.error(`Parent option with key ${parentKey} not found.`);
      return;
    }

    // 자식 옵션 찾기 및 정제
    const childOption = parentOption.origin.option.find(
      (child) => child.option_key.toString() === childKey
    );
    if (!childOption) {
      console.error(
        `Child option with key ${childKey} not found in parent ${parentKey}.`
      );
      return;
    }

    const selectedChildOption = {
      ...parentOption.origin,
      option: childOption,
    };

    // 이미 선택된 옵션인지 확인
    const existingIndex = selectedOption.findIndex(
      (option) => option.option_id === selectedChildOption.option_id
    );

    // 상태 업데이트
    setSelectedOption((prev) => {
      if (existingIndex === -1) {
        // 새로운 옵션 추가
        return [...prev, selectedChildOption];
      }

      // 동일한 옵션일 경우 상태 업데이트 생략
      if (prev[existingIndex].option === selectedChildOption.option) {
        return prev;
      }

      // 기존 옵션 업데이트
      const updatedOptions = [...prev];
      updatedOptions[existingIndex] = selectedChildOption;
      return updatedOptions;
    });
  };

  /* ===== Render ===== */
  return (
    <React.Fragment>
      <ModalLayout
        open={!!data}
        type="modal"
        onCancel={onClose}
        width={520}
        centered
        styles={{
          content: {
            ...styles.layoutStyle,
          },
        }}
      >
        <Content maxWidth={'100%'}>
          <div style={{ ...styles.productWrap }}>
            <div style={{ ...styles.thumbnail }}>
              <Image src={data?.src} />
            </div>
            <Text size={16} weight={700} maxLine={2}>
              {data?.title}
            </Text>
          </div>

          {type !== 'cart' ? null : (
            <div>
              <div style={{ ...styles.optionWrap }}>
                {selectedOption &&
                ProductOptions &&
                ProductOptions.REQUIRED.length ? (
                  <React.Fragment>
                    <p style={{ ...styles.optionTitle, marginTop: 16 }}>
                      필수옵션
                    </p>
                    <div style={{ ...styles.optionContainer }}>
                      {ProductOptions.REQUIRED.map((option, index) => (
                        <Select
                          key={`REQUIRED_${index}`}
                          placeholder={`${option.title}`}
                          options={option.children}
                          style={{ ...styles.option }}
                          size="large"
                          onChange={(e) => handleOptionSelect('REQUIRED', e)}
                          disabled={
                            index === 0 ||
                            (index !== 0 && selectedOption[index - 1])
                              ? false
                              : true
                          }
                        />
                      ))}
                    </div>
                  </React.Fragment>
                ) : null}

                {ProductOptions && ProductOptions.ADDITION.length ? (
                  <React.Fragment>
                    <p style={{ ...styles.optionTitle, marginTop: 8 }}>
                      추가옵션
                    </p>
                    <div style={{ ...styles.optionContainer }}>
                      {ProductOptions.ADDITION.map((option, index) => (
                        <Select
                          key={`ADDITION_${index}`}
                          placeholder={`${option.title}`}
                          options={option.children}
                          style={{ ...styles.option }}
                          size="large"
                          onChange={(e) => handleOptionSelect('ADDITION', e)}
                        />
                      ))}
                    </div>
                  </React.Fragment>
                ) : null}
              </div>

              {/* 정기배송 주기 */}
              {data.type === 'SUBSCRIPTION' ? (
                <Flex vertical gap={4} style={{ marginTop: 16 }}>
                  <Flex vertical>
                    <p style={{ ...styles.optionTitle }}>배송주기</p>
                    <div style={{ ...styles.optionContainer }}>
                      <Select
                        placeholder={`배송주기를 선택해주세요`}
                        options={data?.regularCycle}
                        style={{ ...styles.option }}
                        size="large"
                        value={regularCycle?.regular_cycle}
                        onChange={(e) =>
                          setRegularCycle((prev) => ({
                            ...prev,
                            regular_cycle: e,
                          }))
                        }
                      />
                    </div>
                  </Flex>

                  <Flex vertical>
                    <p style={{ ...styles.optionTitle }}>배송횟수</p>
                    <div style={{ ...styles.optionContainer }}>
                      <Select
                        placeholder={`배송횟수를 선택해주세요`}
                        options={data?.maxRegularCount
                          ?.split(':')
                          ?.map((item) => ({
                            label:
                              Number(item) === 0 ? `제한없음` : `${item}회`,
                            value: Number(item),
                          }))}
                        style={{ ...styles.option }}
                        size="large"
                        value={regularCycle?.max_regular_delivery_count}
                        onChange={(e) =>
                          setRegularCycle((prev) => ({
                            ...prev,
                            max_regular_delivery_count: e,
                          }))
                        }
                      />
                    </div>
                  </Flex>
                </Flex>
              ) : null}

              {/* 수량/금액 */}
              <Content
                maxWidth={'100%'}
                padding={'30px 0px 0px'}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <Modifier
                  minNum={1}
                  maxNum={99}
                  unit={null}
                  init={cartNum}
                  onIncrease={() => setCartNum((prev) => (prev += 1))}
                  onDecrease={() => setCartNum((prev) => (prev -= 1))}
                  layoutStyle={{
                    width: 122,
                    height: 36,
                    background: 'none',
                    border: 'none',
                  }}
                  buttonStyle={{
                    border: 'none',
                  }}
                  readOnly
                />
                <Text size={20} weight={700}>
                  {stringToMoneyFormat(productPrice * cartNum)}
                </Text>
              </Content>
            </div>
          )}
        </Content>

        <Content maxWidth={'100%'} style={{ ...styles.buttonWrap }}>
          <Button onClick={() => onClose()} style={{ ...styles.button }}>
            닫기
          </Button>
          <Button
            type="primary"
            style={{ ...styles.button }}
            onClick={() =>
              handleSubmit(type, {
                ...data,
                cartNum: cartNum,
                product_option: selectedOption,
                regular_cycle: regularCycle,
              })
            }
          >
            {type === 'cart' ? '장바구니 담기' : '찜하기'}
          </Button>
        </Content>
      </ModalLayout>
    </React.Fragment>
  );
};

export default ProductCartModal;
